import { ApiError } from '@/api/errors';

/**
 * Module-level seed-endpoint availability flag (PTV2-1021, dev-decisions D2).
 *
 * `POST /tenants/{uuid}/seed` returns 404 in TWO distinct cases:
 *   1. The endpoint is disabled for the environment
 *      (`PTV2_TENANT_SEED_ENDPOINT_ENABLED=false`) — the backend returns
 *      `ResponseEntity.notFound().build()`, i.e. an EMPTY body with no error
 *      envelope. This is a session-global "feature off" fact, so we latch it
 *      and hide the Seed action everywhere thereafter.
 *   2. The tenant does not exist — the backend returns the standard error
 *      envelope (a 404 with a `message`/`detail`/`code`). This is a transient,
 *      per-call error and must NOT latch the global flag; the caller surfaces a
 *      per-call toast instead.
 *
 * We discriminate on the envelope: the http layer only synthesizes the literal
 * fallback message `HTTP 404` (and no correlationId / fieldViolations) when the
 * response body is empty. An enveloped 404 always carries a real message.
 */

let seedEndpointAvailable = true;

export function isSeedAvailable(): boolean {
  return seedEndpointAvailable;
}

/** Call when the disabled-gate 404 is seen — hides the action thereafter. */
export function markSeedUnavailable(): void {
  seedEndpointAvailable = false;
}

/**
 * True iff `error` is the empty-body, disabled-gate 404 (and so the global
 * flag should latch). An enveloped 404 (tenant-not-found) returns false.
 */
export function isSeedDisabledGate404(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 404) return false;
  // Empty-body 404 → http layer falls back to `HTTP 404` with no envelope
  // signals. Enveloped 404 → a specific message and/or correlationId.
  const hasEnvelope =
    error.correlationId !== undefined ||
    (error.fieldViolations !== undefined && error.fieldViolations.length > 0) ||
    error.message !== 'HTTP 404';
  return !hasEnvelope;
}

/** Test-only reset so specs don't leak the flag between cases. */
export function __resetSeedAvailabilityForTests(): void {
  seedEndpointAvailable = true;
}
